import { useState } from "react";
import { Calendar, Clock, Send, CheckCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  { value: "8am-10am", label: "8:00 AM - 10:00 AM" },
  { value: "10am-12pm", label: "10:00 AM - 12:00 PM" },
  { value: "12pm-2pm", label: "12:00 PM - 2:00 PM" },
  { value: "2pm-4pm", label: "2:00 PM - 4:00 PM" },
  { value: "4pm-6pm", label: "4:00 PM - 6:00 PM" },
];

export default function BookingSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [date, setDate] = useState(null);
  const [formLoadedAt] = useState(() => Date.now() / 1000);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city_state: "",
    business_name: "",
    service_type: "",
    system_type: "",
    number_of_taps: "",
    preferred_time_slot: "",
    notes: "",
    website: "",  // honeypot
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error("Please select a preferred date");
      return;
    }
    
    if (!formData.preferred_time_slot) {
      toast.error("Please select a preferred time slot");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city_state: formData.city_state || null,
        business_name: formData.business_name,
        service_type: formData.service_type,
        system_type: formData.system_type || null,
        number_of_taps: formData.number_of_taps ? parseInt(formData.number_of_taps) : null,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time_slot: formData.preferred_time_slot,
        notes: formData.notes || null,
        website: formData.website,
        form_loaded_at: formLoadedAt,
      };

      await axios.post(`${API_URL}/bookings`, payload);
      
      setSubmitted(true);
      toast.success("Booking request submitted! We'll confirm your appointment soon.");
      
      setTimeout(() => {
        setSubmitted(false);
        setDate(null);
        setFormData({
          name: "",
          email: "",
          phone: "",
          business_name: "",
          service_type: "",
          system_type: "",
          number_of_taps: "",
          preferred_time_slot: "",
          notes: "",
        });
      }, 5000);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Disable past dates and weekends (if needed)
  const disabledDays = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return day < today;
  };

  return (
    <section id="booking" className="py-24 bg-[#0A0A0A]" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          className="text-center mb-16"
        >
          <p className="overline mb-4" data-testid="booking-overline">Schedule Service</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-testid="booking-heading">
            Book Your <span className="gold-text">Service Appointment</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            Choose your preferred date and time, and we'll promptly confirm your appointment.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* HubSpot Meetings Info */}
          <div
          >
            {/* Service Info */}
            <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-8">
              <h3 className="font-heading text-xl text-white mb-6">What to Expect</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#D4AF37] font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">Submit Your Request</p>
                    <p className="text-[#A1A1AA] text-sm">Fill out the form with your preferred date and time</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#D4AF37] font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">We Confirm</p>
                    <p className="text-[#A1A1AA] text-sm">Prompt confirmation from staff during business hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#D4AF37] font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">Service Day</p>
                    <p className="text-[#A1A1AA] text-sm">Our technician arrives on time, fully equipped</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div
          >
            <div className="bg-[#1A1A1A] border border-[#27272A] rounded-2xl p-8">
              {submitted ? (
                <div
                  className="text-center py-12"
                  data-testid="booking-success"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-2">Booking Request Received!</h3>
                  <p className="text-[#A1A1AA]">
                    We'll review your request and confirm your appointment within 2 business hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="booking-name" className="text-[#A1A1AA] mb-2 block">Your Name *</Label>
                      <Input
                        id="booking-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="input-dark"
                        placeholder=""
                        data-testid="booking-input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="booking-email" className="text-[#A1A1AA] mb-2 block">Email *</Label>
                      <Input
                        id="booking-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="input-dark"
                        placeholder=""
                        data-testid="booking-input-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="booking-phone" className="text-[#A1A1AA] mb-2 block">Phone *</Label>
                      <Input
                        id="booking-phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="input-dark"
                        placeholder=""
                        data-testid="booking-input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="booking-city-state" className="text-[#A1A1AA] mb-2 block">City & State *</Label>
                      <Input
                        id="booking-city-state"
                        name="city_state"
                        value={formData.city_state}
                        onChange={handleInputChange}
                        required
                        className="input-dark"
                        placeholder=""
                        data-testid="booking-input-city-state"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="booking-business" className="text-[#A1A1AA] mb-2 block">Business Name *</Label>
                    <Input
                      id="booking-business"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      required
                      className="input-dark"
                      placeholder=""
                      data-testid="booking-input-business"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA] mb-2 block">Service Type *</Label>
                      <Select
                        value={formData.service_type}
                        onValueChange={(value) => handleSelectChange("service_type", value)}
                        required
                      >
                        <SelectTrigger className="input-dark" data-testid="booking-select-service">
                          <SelectValue placeholder="Select service..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cleaning">Line Cleaning</SelectItem>
                          <SelectItem value="installation">System Installation</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="repair">Emergency Repair</SelectItem>
                          <SelectItem value="consultation">On-Site Consultation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA] mb-2 block">System Type</Label>
                      <Select
                        value={formData.system_type}
                        onValueChange={(value) => handleSelectChange("system_type", value)}
                      >
                        <SelectTrigger className="input-dark" data-testid="booking-select-system">
                          <SelectValue placeholder="Select system..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="long_draw">Long Draw</SelectItem>
                          <SelectItem value="short_draw">Short Draw</SelectItem>
                          <SelectItem value="kegerator">Kegerator</SelectItem>
                          <SelectItem value="not_sure">Not Sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA] mb-2 block">Preferred Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="input-dark w-full px-3 py-2 text-left flex items-center justify-between"
                            data-testid="booking-date-picker"
                          >
                            {date ? format(date, "PPP") : <span className="text-[#71717A]">Pick a date</span>}
                            <Calendar className="w-4 h-4 text-[#A1A1AA]" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-[#27272A]" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={disabledDays}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA] mb-2 block">Preferred Time *</Label>
                      <Select
                        value={formData.preferred_time_slot}
                        onValueChange={(value) => handleSelectChange("preferred_time_slot", value)}
                      >
                        <SelectTrigger className="input-dark" data-testid="booking-select-time">
                          <SelectValue placeholder="Select time slot..." />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="booking-taps" className="text-[#A1A1AA] mb-2 block">Number of Taps</Label>
                    <Input
                      id="booking-taps"
                      name="number_of_taps"
                      type="number"
                      min="1"
                      value={formData.number_of_taps}
                      onChange={handleInputChange}
                      className="input-dark"
                      placeholder=""
                      data-testid="booking-input-taps"
                    />
                  </div>

                  <div>
                    <Label htmlFor="booking-notes" className="text-[#A1A1AA] mb-2 block">Additional Notes</Label>
                    <Textarea
                      id="booking-notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      className="input-dark min-h-[80px]"
                      placeholder=""
                      data-testid="booking-input-notes"
                    />
                  </div>

                  {/* Honeypot - hidden from humans */}
                  <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gold w-full flex items-center justify-center space-x-2 disabled:opacity-50"
                    data-testid="booking-submit"
                  >
                    {isSubmitting ? (
                      <span>Submitting...</span>
                    ) : (
                      <>
                        <span>Request Appointment</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
